import { Link, useNavigate } from "@tanstack/react-router"
import { Search, X } from "lucide-react"
import { Dialog } from "radix-ui"
import { useMemo, useState } from "react"
import { useT } from "@/lib/i18n"
import { useDefinitions, useObjects } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { objectTypes } from "@/routes/objects"
import { ErrorState } from "@/components/states"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

/** Search only the cell's authorized projections. Exact keys can be opened directly. */
export function GraphObjectSearch() {
  const [open, setOpen] = useState(false)
  const t = useT()
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button type="button" className="flex h-8 w-full items-center gap-2 rounded-md border border-border-subtle bg-background px-2 text-xs text-muted-foreground hover:border-border hover:text-foreground">
          <Search className="size-3.5" aria-hidden /> {t("graph.search")}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed top-[12vh] left-1/2 z-50 flex max-h-[75vh] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 flex-col rounded-xl border border-border bg-popover shadow-pop outline-none">
          <div className="flex items-center gap-3 border-b border-border-subtle px-4 py-3">
            <Search className="size-4 text-primary" aria-hidden />
            <Dialog.Title className="flex-1 text-sm font-medium">{t("graph.search")}</Dialog.Title>
            <Dialog.Close asChild><Button size="icon" variant="ghost" aria-label={t("graph.closeSearch")}><X /></Button></Dialog.Close>
          </div>
          <Dialog.Description className="px-4 pt-3 text-xs text-muted-foreground">{t("graph.searchHelp")}</Dialog.Description>
          {open ? <SearchResults onOpen={() => setOpen(false)} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function SearchResults({ onOpen }: { onOpen: () => void }) {
  const scope = useScope()
  const defs = useDefinitions(scope)
  const t = useT()
  const types = useMemo(() => defs.data ? objectTypes(defs.data) : [], [defs.data])
  const [chosen, setChosen] = useState("")
  const objectType = chosen || types[0]?.name || ""
  const [query, setQuery] = useState("")
  return (
    <>
      <div className="grid gap-2 px-4 pt-3 sm:grid-cols-2">
        <label className="grid gap-1 text-xs text-muted-foreground">
          {t("graph.searchType")}
          <select aria-label={t("graph.searchType")} value={objectType} onChange={(event) => setChosen(event.target.value)} className="h-8 min-w-0 rounded-md border border-input bg-background px-2 text-xs text-foreground">
            {types.map((type) => <option key={type.name} value={type.name}>{type.name}</option>)}
          </select>
        </label>
        <label className="grid gap-1 text-xs text-muted-foreground">
          {t("graph.searchKey")}
          <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("graph.searchPlaceholder")} />
        </label>
      </div>
      {defs.isError ? <div className="px-4"><ErrorState error={defs.error} what={t("objects.objectTypes")} inline /></div> : null}
      {objectType ? <ObjectResults key={objectType} objectType={objectType} query={query} onOpen={onOpen} /> : <p className="p-4 text-xs text-muted-foreground">{t("states.loading")}</p>}
    </>
  )
}

function ObjectResults({ objectType, query, onOpen }: { objectType: string; query: string; onOpen: () => void }) {
  const scope = useScope()
  const t = useT()
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const page = useObjects(scope, objectType, cursor)
  const filter = query.toLocaleLowerCase()
  const results = page.data?.items.filter((object) => object.key.toLocaleLowerCase().includes(filter)) ?? []
  const openKey = () => {
    if (!query.trim()) return
    void navigate({ to: "/$orgId/$environmentId/graph", params: scope, search: { root: `${objectType}:${query.trim()}` } })
    onOpen()
  }
  return (
    <>
      <div className="mt-3 min-h-24 overflow-y-auto border-t border-border-subtle p-2">
        {page.isError ? <ErrorState error={page.error} what={objectType} inline /> : page.isPending ? <p className="p-3 text-xs text-muted-foreground">{t("states.loading")}</p> : results.length ? results.map((object) => (
          <Link key={object.key} to="/$orgId/$environmentId/graph" params={scope} search={{ root: `${object.objectType}:${object.key}` }} onClick={onOpen} className="flex items-center gap-2 rounded-md px-3 py-2 hover:bg-raised focus-visible:bg-raised">
            <span className="min-w-0 flex-1 truncate font-mono text-xs">{object.key}</span>
            <span className="text-2xs text-muted-foreground">{t("graph.open")}</span>
          </Link>
        )) : <p className="p-3 text-xs text-muted-foreground">{t("graph.searchEmpty")}</p>}
      </div>
      <footer className="flex flex-wrap items-center gap-2 border-t border-border-subtle px-4 py-3">
        {query.trim() ? <Button size="sm" onClick={openKey}>{t("graph.openKey")}</Button> : null}
        <span className="flex-1 text-2xs text-subtle-foreground">{t("graph.searchPage")}</span>
        <Button size="sm" disabled={!cursor || page.isFetching} onClick={() => setCursor(null)}>{t("graph.firstPage")}</Button>
        <Button size="sm" disabled={!page.data?.nextCursor || page.isFetching} onClick={() => setCursor(page.data?.nextCursor ?? null)}>{t("common.next")}</Button>
      </footer>
    </>
  )
}
